(() => {
  const namespace = 'JMGOUsedRefurbishedSplit';
  if (window[namespace]) {
    window[namespace].boot();
    return;
  }

  const mainSelector = '[data-jmgo-ur-part="main"][data-jmgo-ur-product]';
  const controllers = new Map();
  const rememberedSelections = new Map();
  const textOr = (value, fallback = '') =>
    value != null && String(value).trim() !== '' ? String(value) : fallback;

  const escapeHtml = (value) => String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');

  const normaliseList = (value) => {
    if (Array.isArray(value)) return value.filter(Boolean).map(String);
    if (typeof value !== 'string' || !value.trim()) return [];

    return value
      .split(/[,、;|\n]/)
      .map((item) => item.trim())
      .filter(Boolean);
  };

  const accessoryIcon = (name) => {
    if (name.includes('リモコン')) {
      return '<svg viewBox="0 0 48 48" aria-hidden="true"><rect x="15" y="4" width="18" height="40" rx="5"/><circle cx="24" cy="13" r="2"/><path d="M20 23h8M24 19v8M20 33h8"/></svg>';
    }

    if (name.includes('ケーブル') || name.includes('電源')) {
      return '<svg viewBox="0 0 48 48" aria-hidden="true"><path d="M31.56,29.59V13c0-1.66-1.34-3-3-3h-3.07c-1.66,0-3,1.34-3,3v22c0,1.66-1.34,3-3,3h-3.07c-1.66,0-3-1.34-3-3V10"/><path d="M31.54,29.59h.05c1.66,0,3,1.34,3,3v5.41h-6.05v-5.41c0-1.66,1.34-3,3-3Z"/></svg>';
    }

    if (name.includes('アダプター') || name.includes('電源')) {
      return '<svg viewBox="0 0 48 48" aria-hidden="true"><rect x="10" y="14" width="25" height="22" rx="3"/><path d="M35 25h5c3 0 4 2 4 5v6M17 14V8m11 6V8"/></svg>';
    }

    if (name.includes('ケース')) {
      return '<svg viewBox="0 0 48 48" aria-hidden="true"><rect x="6" y="14" width="36" height="27" rx="5"/><path d="M17 14V9h14v5M6 25h36"/></svg>';
    }

    if (name.includes('説明書') || name.includes('ガイド')) {
      return '<svg viewBox="0 0 48 48" aria-hidden="true"><path d="M10 7h20l8 8v26H10z"/><path d="M30 7v8h8M16 24h16M16 31h16"/></svg>';
    }

    if (name.includes('本体')) {
      return '<svg viewBox="0 0 48 48" aria-hidden="true"><rect x="6" y="10" width="36" height="26" rx="6"/><circle cx="31" cy="23" r="7"/><path d="M14 39h20"/></svg>';
    }

    return '<svg viewBox="0 0 48 48" aria-hidden="true"><circle cx="24" cy="24" r="18"/><path d="M16 24h16M24 16v16"/></svg>';
  };

  const initialise = (root) => {
    if (!root || controllers.has(root)) return;

    let variants = {};

    try {
      variants = JSON.parse(
        root.querySelector('[data-variant-json]')?.textContent || '{}'
      );
    } catch (error) {
      console.error('JMGO used/refurbished variant data error', error);
      return;
    }

    const productKey = root.dataset.jmgoUrProduct;
    const cleanup = [];
    const timers = new Set();
    let destroyed = false;
    let mobileBuybarFrame = 0;
    let adding = false;

    const listen = (target, type, callback, options) => {
      if (!target?.addEventListener) return;
      target.addEventListener(type, callback, options);
      cleanup.push(() => target.removeEventListener(type, callback, options));
    };

    const later = (callback, delay) => {
      const timer = window.setTimeout(() => {
        timers.delete(timer);
        if (!destroyed) callback();
      }, delay);
      timers.add(timer);
    };

    // Find current DOM nodes every time: the theme editor can replace any section.
    const detailSections = () =>
      [...document.querySelectorAll('[data-jmgo-ur-product][data-jmgo-ur-part]')]
        .filter((section) =>
          section.dataset.jmgoUrProduct === productKey &&
          section.dataset.jmgoUrPart !== 'main'
        );

    const cards = [...root.querySelectorAll('[data-variant-card]')];
    const filterButtons = [...root.querySelectorAll('[data-filter]')];
    const variantInput = root.querySelector('[data-variant-input]');
    const form = root.querySelector('[data-product-form]');
    const addButton = root.querySelector('[data-add-button]');
    const errorBox = root.querySelector('[data-cart-error]');
    const sortSelect = root.querySelector('[data-sort]');
    const mainImage = root.querySelector('.jmgo-ur__main-image img');
    const thumbnails = root.querySelector('[data-thumbnails]');
    const list = root.querySelector('[data-variant-list]');
    const visibleEmpty = root.querySelector('[data-filter-empty]');
    const chooser = root.querySelector('.jmgo-ur__chooser');
    const normalBuybox = root.querySelector('.jmgo-ur__buybox');
    const mobileBuybar = root.querySelector('[data-mobile-buybar]');
    const mobileAddButton = root.querySelector('[data-mobile-add-button]');
    const mobileViewport = window.matchMedia('(max-width: 560px)');

    let currentFilter = 'all';
    const rememberedId = rememberedSelections.get(productKey);
    let selectedId =
      (rememberedId && variants[rememberedId]?.available ? rememberedId : '') ||
      root.dataset.initialVariantId ||
      variantInput?.value ||
      cards[0]?.dataset.variantId ||
      '';

    const readSelection = () => {
      if (destroyed || !root.isConnected) return null;
      const variant = variants[String(selectedId)];
      if (!variant) return null;
      return {
        productId: String(productKey),
        variantId: String(variant.id),
        category: variant.category,
        productType: variant.type
      };
    };

    const notifySelection = () => {
      const detail = readSelection();
      if (!detail) return;
      document.dispatchEvent(new CustomEvent('jmgo:used-refurbished:variant-change', {
        detail
      }));
    };

    const setText = (selector, value) => {
      const element = root.querySelector(selector);
      if (element) element.textContent = value ?? '';
    };

    const setAllText = (selector, value) => {
      root.querySelectorAll(selector).forEach((element) => {
        element.textContent = value ?? '';
      });
    };

    const setAddButtonState = (text, disabled) => {
      if (addButton) {
        addButton.disabled = disabled;
        const addLabel = addButton.querySelector('[data-add-label]');
        if (addLabel) addLabel.textContent = text;
      }

      if (mobileAddButton) {
        mobileAddButton.disabled = disabled;
        const mobileLabel = mobileAddButton.querySelector('[data-mobile-add-label]');
        if (mobileLabel) mobileLabel.textContent = text;
      }
    };

    const publishAddToCart = (lineItem, variant) => {
      const quantity = Number(lineItem?.quantity || 1);
      const priceRaw = Number(
        lineItem?.final_price ??
        lineItem?.price ??
        variant?.priceRaw ??
        0
      );
      const price = priceRaw / 100;
      const currency =
        window.Shopify?.currency?.active ||
        root.dataset.currency || '';
      const itemId =
        lineItem?.sku ||
        variant?.unitNumber ||
        String(variant?.id || selectedId);
      const itemName =
        lineItem?.product_title ||
        lineItem?.title ||
        root.dataset.productTitle || '';
      const itemVariant =
        lineItem?.variant_title ||
        variant?.title ||
        '';

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        event: 'add_to_cart',
        ecommerce: {
          currency,
          value: price * quantity,
          items: [
            {
              item_id: itemId,
              item_name: itemName,
              item_variant: itemVariant,
              item_category: variant?.type || '',
              price,
              quantity
            }
          ]
        }
      });

      document.dispatchEvent(
        new CustomEvent('jmgo:add-to-cart', {
          detail: {
            variantId: String(variant?.id || selectedId),
            unitNumber: variant?.unitNumber || '',
            price,
            quantity,
            currency
          }
        })
      );
    };

    const renderGallery = (variant) => {
      const gallery = Array.isArray(variant.gallery)
        ? variant.gallery.filter((item) => item?.src)
        : [];

      if (!gallery.length) return;

      if (mainImage) {
        mainImage.removeAttribute('srcset');
        mainImage.removeAttribute('sizes');
        mainImage.src = gallery[0].src;
        mainImage.alt = gallery[0].alt || variant.title;
      }

      if (thumbnails) {
        thumbnails.innerHTML = gallery
          .map(
            (item, index) => `
              <button
                type="button"
                class="jmgo-ur__thumb"
                data-thumb-index="${index}"
                aria-current="${index === 0 ? 'true' : 'false'}"
                aria-label="画像${index + 1}を表示"
              >
                <img
                  src="${escapeHtml(item.thumb || item.src)}"
                  alt="${escapeHtml(item.alt || variant.title)}"
                  loading="lazy"
                >
              </button>
            `
          )
          .join('');
      }

    };

    const renderAccessories = (value, accessoriesGrid) => {
      if (!accessoriesGrid) return;

      const accessories = normaliseList(value);

      if (!accessories.length) {
        accessoriesGrid.innerHTML =
          '<p class="jmgo-ur__empty">この個体の付属品情報は準備中です。</p>';
        return;
      }

      accessoriesGrid.innerHTML = accessories
        .map(
          (name) => `
            <div class="jmgo-ur__accessory-item">
              <div class="jmgo-ur__accessory-icon">
                ${accessoryIcon(name)}
              </div>
              <div class="jmgo-ur__accessory-name">
                ${escapeHtml(name)}
              </div>
            </div>
          `
        )
        .join('');
    };

    const renderDetails = (variant) => {
      if (!variant) return;
      const gallery = Array.isArray(variant.gallery)
        ? variant.gallery.filter((item) => item?.src)
        : [];
      const stateGallery = gallery.length > 1 ? gallery.slice(1, 3) : gallery.slice(0, 1);

      detailSections().forEach((section) => {
        const setDetailText = (selector, value) => {
          section.querySelectorAll(selector).forEach((element) => {
            element.textContent = value ?? '';
          });
        };

        if (section.dataset.jmgoUrPart === 'condition') {
          const rank = textOr(
            variant.rankRaw,
            variant.category === 'refurbished'
              ? section.dataset.defaultRefurbishedLabel
              : variant.title
          );
          setDetailText('[data-state-rank]', `${variant.type} ／ ${rank || ''}`);
          setDetailText('[data-state-unit]', `個体番号：${variant.unitNumber}`);
          setDetailText('[data-state-note]', textOr(
            variant.noteRaw,
            section.dataset.defaultConditionNote || ''
          ));
          section.querySelectorAll('[data-state-badge]').forEach((badge) => {
            badge.textContent = variant.type;
            badge.classList.toggle('jmgo-ur__badge--refurbished', variant.category === 'refurbished');
          });
          section.querySelectorAll('[data-condition-images]').forEach((images) => {
            images.innerHTML = stateGallery.map((item, index) => `
              <figure class="jmgo-ur__condition-photo">
                <img src="${escapeHtml(item.src)}" alt="${escapeHtml(item.alt || `${variant.title}の状態写真`)}" loading="lazy">
                <figcaption>${index === 0 ? '個体の状態' : '傷・使用感の確認'}</figcaption>
              </figure>
            `).join('');
          });
        }

        if (section.dataset.jmgoUrPart === 'accessories') {
          section.querySelectorAll('[data-accessories]').forEach((grid) => {
            renderAccessories(variant.accessories, grid);
          });
        }

        if (section.dataset.jmgoUrPart === 'warranty') {
          setDetailText('[data-selected-warranty]', textOr(
            variant.warrantyRaw,
            section.dataset.defaultWarranty || ''
          ));
        }
      });
    };

    const refreshTabs = () => {
      const tabs = [...root.querySelectorAll('.jmgo-ur__tab')];
      tabs.forEach((tab) => {
        const target = document.getElementById(tab.hash.slice(1));
        const owner = target?.closest('[data-jmgo-ur-product]');
        tab.hidden = !target || owner?.dataset.jmgoUrProduct !== productKey;
      });
      const navigation = root.querySelector('.jmgo-ur__tabs');
      if (navigation) navigation.hidden = !tabs.some((tab) => !tab.hidden);
    };

    const selectVariant = (id, updateUrl = true) => {
      const variant = variants[String(id)];
      if (!variant || !variant.available) return;

      selectedId = String(variant.id);
      rememberedSelections.set(productKey, selectedId);

      if (variantInput) {
        variantInput.value = variant.id;
      }

      cards.forEach((card) => {
        const selected = card.dataset.variantId === selectedId;

        card.classList.toggle('is-selected', selected);
        card.setAttribute('aria-selected', selected ? 'true' : 'false');
      });

      setText(
        '[data-selected-line]',
        `選択中：${variant.type} ／ ${variant.unitNumber} ／ ${variant.rank}`
      );
      setText('[data-selected-price]', variant.price);
      setText(
        '[data-mobile-buybar-summary]',
        `${variant.type} ／ ${variant.unitNumber} ／ ${variant.rank}`
      );
      setText('[data-mobile-buybar-price]', variant.price);
      setAllText('[data-selected-warranty]', variant.warranty);
      setText(
        '[data-gallery-caption]',
        `選択中の個体：${variant.unitNumber}`
      );
      renderGallery(variant);
      renderDetails(variant);

      setAddButtonState(
        root.dataset.cartLabel || 'この個体をカートに追加',
        adding
      );

      if (errorBox) {
        errorBox.hidden = true;
      }

      if (updateUrl && window.history?.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      }

      notifySelection();
      scheduleMobileBuybar();
    };

    const applyFilter = () => {
      let shown = 0;

      cards.forEach((card) => {
        const visible =
          currentFilter === 'all' ||
          card.dataset.category === currentFilter;

        card.hidden = !visible;

        if (visible) {
          shown += 1;
        }
      });

      if (visibleEmpty) {
        visibleEmpty.hidden = shown !== 0;
      }
    };

    const applySort = () => {
      if (!list || !sortSelect) return;

      const sorted = [...cards].sort((a, b) => {
        switch (sortSelect.value) {
          case 'price-desc':
            return Number(b.dataset.price) - Number(a.dataset.price);

          case 'price-asc':
          default:
            return Number(a.dataset.price) - Number(b.dataset.price);
        }
      });

      sorted.forEach((card) => {
        list.appendChild(card);
      });

      applyFilter();
    };

    filterButtons.forEach((button) => {
      listen(button, 'click', () => {
        currentFilter = button.dataset.filter;

        filterButtons.forEach((candidate) => {
          const active = candidate === button;

          candidate.classList.toggle('is-active', active);
          candidate.setAttribute(
            'aria-pressed',
            active ? 'true' : 'false'
          );
        });

        applyFilter();

        const selectedCard = cards.find(
          (card) => card.dataset.variantId === selectedId
        );

        if (selectedCard?.hidden) {
          const firstVisible = [
            ...list.querySelectorAll('[data-variant-card]')
          ].find((card) => !card.hidden);

          if (firstVisible) {
            selectVariant(firstVisible.dataset.variantId);
          }
        }
      });
    });

    cards.forEach((card) => {
      listen(card, 'click', () => {
        selectVariant(card.dataset.variantId);
      });
    });

    listen(sortSelect, 'change', applySort);

    listen(mobileAddButton, 'click', () => {
      if (!form || mobileAddButton.disabled) return;

      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit();
      } else {
        addButton?.click();
      }
    });

    let mobileBuybarTicking = false;

    const updateMobileBuybar = () => {
      if (!mobileBuybar || destroyed) return;

      if (
        !mobileViewport.matches ||
        !chooser ||
        !normalBuybox
      ) {
        mobileBuybar.classList.remove('is-visible');
        mobileBuybar.hidden = true;
        mobileBuybar.setAttribute('aria-hidden', 'true');
        return;
      }

      mobileBuybar.hidden = false;

      const chooserRect = chooser.getBoundingClientRect();
      const buyboxRect = normalBuybox.getBoundingClientRect();
      const firstDetail = detailSections()[0];
      const details = firstDetail?.querySelector('.jmgo-ur__details') || firstDetail;
      const detailBoundary = details
        ? details.getBoundingClientRect().top
        : root.getBoundingClientRect().bottom;

      const chooserStarted =
        chooserRect.top <= window.innerHeight * 0.72;
      const normalBuyboxVisible =
        buyboxRect.top < window.innerHeight &&
        buyboxRect.bottom > 0;
      const beforeDetails = detailBoundary > 0;
      const visible =
        chooserStarted &&
        beforeDetails &&
        !normalBuyboxVisible;

      mobileBuybar.classList.toggle('is-visible', visible);
      mobileBuybar.setAttribute(
        'aria-hidden',
        visible ? 'false' : 'true'
      );
    };

    const scheduleMobileBuybar = () => {
      if (mobileBuybarTicking || destroyed) return;

      mobileBuybarTicking = true;

      mobileBuybarFrame = window.requestAnimationFrame(() => {
        mobileBuybarFrame = 0;
        updateMobileBuybar();
        mobileBuybarTicking = false;
      });
    };

    listen(window,
      'scroll',
      scheduleMobileBuybar,
      { passive: true }
    );
    listen(window,
      'resize',
      scheduleMobileBuybar,
      { passive: true }
    );
    listen(mobileViewport,
      'change',
      scheduleMobileBuybar
    );

    listen(thumbnails, 'click', (event) => {
      const button = event.target.closest('[data-thumb-index]');
      if (!button) return;

      const variant = variants[selectedId];
      const item =
        variant?.gallery?.[Number(button.dataset.thumbIndex)];

      if (!item || !mainImage) return;

      mainImage.removeAttribute('srcset');
      mainImage.removeAttribute('sizes');
      mainImage.src = item.src;
      mainImage.alt = item.alt || variant.title;

      thumbnails
        .querySelectorAll('[data-thumb-index]')
        .forEach((thumb) => {
          thumb.setAttribute(
            'aria-current',
            thumb === button ? 'true' : 'false'
          );
        });
    });

    listen(form, 'submit', async (event) => {
      event.preventDefault();

      if (adding || !selectedId || !variants[selectedId]?.available) return;

      adding = true;
      const submittedVariant = variants[selectedId];

      const idleLabel =
        root.dataset.cartLabel ||
        'この個体をカートに追加';

      if (errorBox) {
        errorBox.hidden = true;
      }

      setAddButtonState('追加中…', true);

      try {
        const response = await fetch(
          root.dataset.cartAddUrl || `${window.Shopify?.routes?.root || '/'}cart/add.js`,
          {
            method: 'POST',
            headers: {
              'X-Requested-With': 'XMLHttpRequest',
              Accept: 'application/json'
            },
            body: new FormData(form)
          }
        );

        const result = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(
            result.description ||
            'カートへの追加に失敗しました。'
          );
        }

        publishAddToCart(result, submittedVariant);
        if (destroyed) return;

        setAddButtonState('カートに追加しました', true);

        if (window.ajaxCart?.load) {
          window.ajaxCart.load();
        }

        try {
          const cartResponse = await fetch(
            root.dataset.cartJsonUrl || `${window.Shopify?.routes?.root || '/'}cart.js`,
            {
              headers: {
                Accept: 'application/json'
              },
              cache: 'no-store'
            }
          );

          if (cartResponse.ok) {
            const cart = await cartResponse.json();

            document
              .querySelectorAll(
                '#CartCount, .jmgo-site-header__cart-count'
              )
              .forEach((count) => {
                count.textContent = cart.item_count;
                count.classList.toggle(
                  'hidden-count',
                  cart.item_count === 0
                );
              });

            document
              .querySelectorAll('.jmgo-site-header__cart-link')
              .forEach((link) => {
                const cartLabel =
                  link
                    .querySelector(
                      '.jmgo-site-header__cart-label'
                    )
                    ?.textContent
                    ?.trim() ||
                  'カート';

                link.classList.toggle(
                  'js-cart-full',
                  cart.item_count > 0
                );

                link.setAttribute(
                  'aria-label',
                  `${cartLabel}、商品数 ${cart.item_count}`
                );
              });
          }
        } catch (cartError) {
          console.warn(
            'カート件数の表示更新に失敗しました。',
            cartError
          );
        }

        document.documentElement.dispatchEvent(
          new CustomEvent('cart:refresh', {
            bubbles: true
          })
        );

        later(() => {
          setAddButtonState(idleLabel, false);
        }, 1400);
      } catch (error) {
        if (destroyed) return;
        if (errorBox) {
          errorBox.textContent =
            error.message.includes('sold') ||
            error.message.includes('在庫')
              ? '申し訳ありません。この個体は売り切れました。ページを更新して別の個体をお選びください。'
              : error.message;

          errorBox.hidden = false;
        }

        setAddButtonState('売り切れ', true);

        later(() => {
          window.location.reload();
        }, 2200);
      } finally {
        adding = false;
      }
    });

    const refresh = () => {
      if (destroyed) return;
      renderDetails(variants[selectedId]);
      refreshTabs();
      notifySelection();
      scheduleMobileBuybar();
    };

    const destroy = () => {
      destroyed = true;
      cleanup.forEach((remove) => remove());
      timers.forEach((timer) => window.clearTimeout(timer));
      if (mobileBuybarFrame) window.cancelAnimationFrame(mobileBuybarFrame);
      controllers.delete(root);
    };

    controllers.set(root, { refresh, destroy, getSelection: readSelection });
    applySort();
    if (selectedId && variants[selectedId]?.available) {
      selectVariant(selectedId, false);
    }
    // Also synchronize the selected fallback variant when every unit is sold out.
    refresh();
  };

  const boot = () => {
    for (const [root, controller] of controllers) {
      if (!root.isConnected) controller.destroy();
    }
    document.querySelectorAll(mainSelector).forEach(initialise);
    controllers.forEach((controller) => controller.refresh());
  };

  const getSelection = (productId) => {
    const key = String(productId);
    for (const controller of controllers.values()) {
      const selection = controller.getSelection();
      if (selection?.productId === key) return selection;
    }
    return null;
  };

  window[namespace] = { boot, getSelection };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
  document.addEventListener('shopify:section:reorder', boot);
  document.addEventListener('shopify:section:unload', (event) => {
    for (const [root, controller] of controllers) {
      if (event.target === root || event.target.contains(root)) controller.destroy();
    }
    // Shopify dispatches unload before removing the section from the document.
    window.requestAnimationFrame(boot);
  });
})();
